import { BrandClub, getBrandIds } from "@brandclub/common-ui";
import { Skeleton, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { getRewardAmountsByStatus, RewardsForBrandClub } from "../../../../api";
import { useAppSelector } from "../../../../redux/hooks";
import { isDefined } from "../../../../utils/misc";
import { formatMoney } from "../../../../utils/StringUtils";
import StoreLoadingSkeleton from "../../../StoreComponents/StoreLoadingSkeleton";
import HomeAccountsPreview from "../Pages/Accounts/HomeAccountsPreview";
import AffiliatePreview from "../Pages/Affiliate/preview";
import PreviewWidgetWrapper from "../Pages/components/PreviewWidgetWrapper";
import ContentPreview from "../Pages/Content/preview";
import PopularPreview from "../Pages/Popular/preview";
import ReviewPreview from "../Pages/Review/preview";
import SurveysPreview from "../Pages/Survey/preview";
import TrendingPreview from "../Pages/Trending/preview";
import { PageContext } from "./types";
import { BrandstoreRewardsCarousel } from "./BrandstoreRewardsCarousel";

import {
  BrandHeaderContainer,
  BrandstoreLandingDescription,
  HomePageContainer,
  SearchBar,
} from "./BrandstoreHomePageStyles";
import BrandstoreRewardPillSection from "./BrandstoreRewardPillSection";
import { useSignedIn } from "@/utils/hooks/useSignedIn";
import { useFetchReviews } from "../Pages/Review/data";
import { ApolloClient, useApolloClient } from "@apollo/client";

interface RewardsSummaryTextProps {
  brandName: string;
  rewards?: RewardsForBrandClub;
}
export const RewardsSummaryText = ({
  brandName,
  rewards,
}: RewardsSummaryTextProps) => {
  const theme = useTheme();
  const isTabletView = useMediaQuery(theme.breakpoints.down("md"));
  if (!rewards?.lifetimeEarnings) {
    return (
      <BrandstoreLandingDescription>
        Earn cash rewards when you shop and engage with {brandName}.
      </BrandstoreLandingDescription>
    );
  }
  const lifeTimeEarnings = rewards.lifetimeEarnings || 0;
  const lifeTimePayouts = rewards.lifetimePayouts || 0;
  const lifeTimeAdjustments = rewards.lifetimeAdjustments || 0;
  const currentBalance =
    lifeTimeEarnings - lifeTimePayouts - lifeTimeAdjustments;

  return (
    <BrandstoreLandingDescription>
      You earned {formatMoney(lifeTimeEarnings)} in total rewards and received{" "}
      {formatMoney(lifeTimePayouts)} in payouts, {!isTabletView && <br />}
      so your reward balance is {formatMoney(currentBalance)} with {brandName}.
    </BrandstoreLandingDescription>
  );
};

const loadBrandRewards = async (
  apolloClient: ApolloClient<object>,
  brandClub: BrandClub
) => {
  const isMember = brandClub.isMember;
  const brandIds = brandClub.brandIds;
  if (isMember && brandIds) {
    const variables = { brandIds, forceRefresh: true };
    const { data } = await apolloClient.query({
      query: getRewardAmountsByStatus,
      variables,
    });
    if (data && data.RewardSummary) {
      return data.RewardSummary;
    }
  }
  return undefined;
};

interface BrandstoreHeaderProps {
  brandclub?: BrandClub;
  brandclubRewards?: RewardsForBrandClub;
  loading: boolean;
  brandName: string;
}
const BrandstoreHeader = ({
  brandclub,
  brandclubRewards,
  loading,
  brandName,
}: BrandstoreHeaderProps) => {
  const isLoggedIn = useSignedIn().signedIn;
  const userGivenName = useAppSelector(
    ({ userProfile }) => userProfile?.given_name
  );
  if (loading) {
    return (
      <PreviewWidgetWrapper>
        <BrandHeaderContainer
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Skeleton
            variant="rectangular"
            sx={{
              bgcolor: "#f6f8fa",
              borderRadius: 6,
              marginBottom: 1.25,
              height: 24,
              width: "15%",
            }}
          />
          <Skeleton
            variant="rectangular"
            sx={{
              bgcolor: "#f6f8fa",
              borderRadius: 6,
              marginBottom: 1.25,
              height: 32,
              width: "30%",
            }}
          />
          <Skeleton
            variant="rectangular"
            sx={{
              bgcolor: "#f6f8fa",
              borderRadius: 6,
              marginBottom: 0,
              height: 50,
              width: "80%",
            }}
          />
        </BrandHeaderContainer>
      </PreviewWidgetWrapper>
    );
  }

  const isMember = isLoggedIn && brandclub?.isMember;

  return (
    <PreviewWidgetWrapper>
      <BrandHeaderContainer>
        <div className="brand_landing_name">{brandName}</div>
        <div className="brand_landing_title">
          {isMember ? `Welcome back, ${userGivenName}` : "Join the club"}
        </div>
        <RewardsSummaryText
          brandName={brandName}
          rewards={isMember ? brandclubRewards : undefined}
        />
        {!!isMember && (
          <BrandstoreRewardPillSection rewards={brandclubRewards} />
        )}
        <SearchBar placeholder={`Search ${brandName}`} />
      </BrandHeaderContainer>
    </PreviewWidgetWrapper>
  );
};

export const getIsLoading = ({
  isLoggedIn,
  brandclub,
  loading,
}: {
  isLoggedIn: boolean;
  brandclub: BrandClub | undefined;
  loading: boolean;
}) => {
  return loading || (isLoggedIn && !brandclub);
};

const BrandstoreHomePage = (props: { pageContext: PageContext }) => {
  const brandEntity = useAppSelector(({ brandEntity }) => brandEntity);
  const apolloClient = useApolloClient();
  const isLoggedIn = useSignedIn().signedIn;
  const brandclub = brandEntity?.brandclub;
  const brandIds = useMemo(
    () => getBrandIds(brandEntity?.entity),
    [brandEntity?.entity]
  );
  const brandName = brandEntity?.entity?.name;
  const { data: reviewRewards, loading: loadingReviewRewards } =
    useFetchReviews({
      brandIds,
      pageSize: 25,
    });
  const [loading, setLoading] = useState(false);
  const isLoading =
    getIsLoading({
      isLoggedIn,
      brandclub,
      loading,
    }) ||
    (loadingReviewRewards && !reviewRewards?.length);

  const [brandclubRewards, setBrandclubRewards] =
    useState<RewardsForBrandClub>();

  useEffect(() => {
    const loadReward = async () => {
      if (brandclub) {
        setLoading(true);
        const rewards = await loadBrandRewards(apolloClient, brandclub);
        if (rewards) {
          setBrandclubRewards(rewards);
        }
        setLoading(false);
      }
    };
    loadReward();
  }, [brandclub, apolloClient]);

  const brandclubIds = useMemo(
    () =>
      brandEntity?.entity?.brandclubId != null
        ? [brandEntity.entity.brandclubId]
        : undefined,
    [brandEntity?.entity?.brandclubId]
  );

  return (
    <HomePageContainer>
      <BrandstoreHeader
        brandclub={brandclub}
        brandclubRewards={brandclubRewards}
        loading={isLoading}
        brandName={brandName}
      />
      {isLoading ? (
        <PreviewWidgetWrapper>
          <StoreLoadingSkeleton
            header={false}
            cardStyle="cardForThree"
            skeletonCardProps={{ height: 278, width: 322 }}
          />
        </PreviewWidgetWrapper>
      ) : (
        <BrandstoreRewardsCarousel reviewRewards={reviewRewards} />
      )}
      <PopularPreview
        brandIds={brandIds}
        isFiniteScroll
        pageSize={10}
        title="Most popular"
        subtitle="Shop the best sellers"
      />
      {isDefined(brandclubIds) && (
        <SurveysPreview
          brandclubIds={brandclubIds}
          backgroundColor="white"
          arrowColor="grey"
        />
      )}
      {isDefined(brandIds) && (
        <ReviewPreview
          brandIds={brandIds}
          backgroundColor="white"
          arrowColor="grey"
        />
      )}
      {isDefined(brandIds) && <AffiliatePreview brandIds={brandIds} />}
      {isDefined(brandclubIds) && (
        <ContentPreview
          brandclubIds={brandclubIds}
          backgroundColor="white"
          arrowColor="grey"
        />
      )}
      <HomeAccountsPreview />
      {isDefined(brandIds) && <TrendingPreview brandIds={brandIds} />}
    </HomePageContainer>
  );
};

export default BrandstoreHomePage;
